"use client";

import type React from "react";

import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Autocomplete,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  Share as ShareIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Email as EmailIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  UploadFile as UploadFileIcon,
  CloudUpload,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router";
import { useUser } from "@/contexts/UserContext";
import axios from "axios";
import { apiRemote, IHost, IWorkshopForm, IWorkshopFormEdit } from "@/api";
import { useAlert } from "@/contexts/AlertContext";
import useDebounce from "@/hooks/use-debounce";
import { EditToggle, EditType } from "@/components/EditToggle";

interface Educator {
  ID: string; // Changed from 'id' to 'ID' to match your data
  user_login: string;
  user_pass: string;
  user_nicename: string;
  user_email: string;
  user_url: string;
  user_registered: string;
  user_activation_key: string;
  user_status: string;
  display_name: string; // This is the name you want to display
  status: string;
}

interface Topic {
  id: number;
  name: string;
}

interface AttachedDocument {
  id: number;
  workshop_id: number;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_at: string;
  updated_at: string;
}

type FileOrAttachedDocument = File | AttachedDocument;

const initialWorkshopFormData = {
  id: 0,
  title: "",
  description: "",
  learning_objectives: "",
  target_audience: "",
  host_type: "pythaverse", // Default to pythaverse
  host_name: "",
  host_email: "",
  host_organization: null, // Change to null to match API if it can be null
  host_bio_snippet: null, // Add this if it exists in your API response and you plan to use it
  hosts: [] as IHost[], // Assuming IHost is an interface you have defined elsewhere
  date: "",
  duration: 0, // Changed to number based on API data
  format: "online",
  category: "", // Or a default category ID if applicable
  max_attendee: 0, // Changed to number based on API data
  event_link: "",
  topic: [] as Topic[], // Khởi tạo topic là một mảng rỗng với kiểu Topic[]
  image_url: null, // Changed to image_url to match API data
  // Thêm các trường khác nếu bạn muốn khởi tạo chúng
  resources: null,
  video_recording_url: null,
  discussion_link: null,
  course_url: null,
  status: "",
  is_featured: 0,
  is_public: 1,
  created_by: "",
  created_at: "",
  updated_at: "",
  image_url_old: null,
  attached_documents: [],
   // Các trường cũ
   host_name_old: '',
   host_email_old: '',
   host_organization_old: '',
   host_bio_snippet_old: ''
};

export default function EditWorkshop() {
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [aiImagePrompt, setAiImagePrompt] = useState(""); // State for AI image prompt
  const [selectedImage, setSelectedImage] = useState<string | null>(null); // State for selected image URL

  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isGeneratingObjectives, setIsGeneratingObjectives] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  const [educators, setEducators] = useState<any[]>([]);
  const [searchEducators, setSearchEducators] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchEducators, 500);
  const [selectedEducator, setSelectedEducator] = useState<Educator | null>(
    null
  );
  const [selectedEducators, setSelectedEducators] = useState<Educator[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [topics, setTopics] = useState<any[]>([]);
  const { id } = useParams<{ id: string }>();

  const alert = useAlert();

  const navigate = useNavigate();
  const { user } = useUser();

  const isAdminOrOrg = user?.is_admin || user?.is_org;

  const [workshopData, setWorkshopData] = useState<IWorkshopFormEdit>(initialWorkshopFormData);
  const [loading, setLoading] = useState<boolean>(false);

  if (user?.user_login == null || typeof user?.user_login == "undefined") {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", py: 5 }}>
        <Typography variant="h5" color="error">
          Please login to edit workshop.
        </Typography>
      </Container>
    );
  }

  useEffect(() => {
     // Nếu host_type là "guest", reset mảng selectedEducators
     if (workshopData.host_type === "guest") {
         setSelectedEducators([]);
     }
   }, [workshopData.host_type]);

  useEffect(() => {
    if (workshopData.host_type === "pythaverse") {
        const newHosts: IHost[] = selectedEducators.map(edu => ({
            host_name: edu.display_name,
            host_email: edu.user_email,
            host_organization: "",
            host_bio: ""
        }));
        setWorkshopData(prev => ({ ...prev, hosts: newHosts }));
    }
  }, [selectedEducators, workshopData.host_type]);

  useEffect(() => {
    if (!id) {
      setLoading(false); // No ID, so stop loading
      alert.error("No workshop ID provided for editing.");

      return;
    }
    setLoading(true);
    apiRemote
      .getWorkshopById(id)
      .then((data) => {
        if (data && data.data) {
          const processedDescription = data.data.description.replace(
            /\\'/g,
            "'"
          );
          let parsedTopics: Topic[] = [];
          if (data.data.topic && typeof data.data.topic === "string") {
            try {
              // Parse chuỗi JSON thành mảng các đối tượng topic
              parsedTopics = JSON.parse(data.data.topic);
            } catch (e) {
              console.error("Error parsing topic string:", e);
              alert.error("Failed to parse workshop topics.");
            }
          } else if (Array.isArray(data.data.topic)) {
            // Nếu topic đã là một mảng (trường hợp lý tưởng), gán trực tiếp
            parsedTopics = data.data.topic;
          }

          let parsedHosts: IHost[] = [];
          // Xử lý dữ liệu host: ưu tiên mảng hosts, nếu không có thì tạo từ các trường host cũ
          if (data.data.hosts && data.data.hosts !== "[]") {
              try {
                  parsedHosts = JSON.parse(data.data.hosts);
              } catch (e) {
                  console.error("Error parsing hosts string:", e);
                  alert.error("Failed to parse workshop hosts.");
              }
          } else if (data.data.host_name) {
              // Tương thích ngược: tạo mảng host từ các trường cũ
              parsedHosts = [{
                  host_name: data.data.host_name,
                  host_email: data.data.host_email,
                  host_organization: data.data.host_organization,
                  host_bio: data.data.host_bio_snippet,
              }];
          }

          setWorkshopData({
            ...data.data,
            description: processedDescription,
            topic: parsedTopics, // Gán mảng topics đã parse vào state
            image_url: data.data.image_url, // Ensure 'image' state gets 'image_url' from API
            image_url_old: data.data.image_url,
            attached_documents: data.data.attached_documents || [],
            video_recording_url: data.data.video_recording_url,
            hosts: parsedHosts,
          });

          if (data.data.host_type === "pythaverse" && parsedHosts.length > 0) {
            const educatorsToSelect = parsedHosts.map(host => ({
              ID: host.host_email,
              user_login: "",
              user_pass: "",
              user_nicename: "",
              user_email: host.host_email,
              user_url: "",
              user_registered: "",
              user_activation_key: "",
              user_status: "",
              display_name: host.host_name,
              status: "",
            }));
            setSelectedEducators(educatorsToSelect);
          }
  
          if (data.data.image_url) {
            setSelectedImage(data.data.image_url);
          }
        }
      })
      .catch((err) => {
        alert.error(err.message || "Failed to fetch workshop details.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (searchEducators == "") {
      setEducators([]);
    } else {
      apiRemote
        .getListEducator(searchEducators)
        .then((data) => {
          setEducators(data.data);
        })
        .catch((error) => {
          console.error("Error fetching educators:", error);
          setEducators([]); // Clear educators on error
        });
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    apiRemote
      .getListCategory()
      .then((data) => {
        setCategories(data.data || []);
      })
      .catch((error) => {
        console.error("Error fetching educators:", error);
        setCategories([]); // Clear educators on error
      });
  }, []);

  const handleAddGuest = () => {
    setWorkshopData(prev => ({
      ...prev,
      hosts: [...prev.hosts, {
        host_name: "",
        host_email: "",
        host_organization: "",
        host_bio: ""
      }]
    }));
  };

  const handleRemoveGuest = (index: number) => {
    setWorkshopData(prev => {
      const newHosts = [...prev.hosts];
      newHosts.splice(index, 1);
      return { ...prev, hosts: newHosts };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // alert.success("Workshop Published!")
    if (
      !workshopData.title ||
      !workshopData.description ||
      !workshopData.date ||
      // !workshopData.event_link ||
      !workshopData.topic ||
      workshopData.topic.length === 0 ||
      !workshopData.duration ||
      !workshopData.format ||
      !workshopData.category ||
      !workshopData.max_attendee ||
      !workshopData.learning_objectives || 
      !workshopData.target_audience
    ) {
      alert.error("Please fill in all required information");
      return;
    }

    if (!workshopData.image_url) {
      alert.error("Please upload an image for the workshop.");
      return;
    }

    // Create a FormData object to send both text fields and files
    const formData = new FormData();

    // // Append all workshop data fields to formData
    for (const key in workshopData) {
      // console.log(key, workshopData.hasOwnProperty(key), workshopData)
      if (workshopData.hasOwnProperty(key)) {
        // Assert key as a valid key of workshopData to resolve TypeScript error
        const typedKey = key as keyof typeof workshopData;
        
        // Handle specific cases for non-string data
        if (typedKey === "topic") {
          formData.append(typedKey, JSON.stringify(workshopData[typedKey]));
        } else if (typedKey === "hosts" && Array.isArray(workshopData[typedKey])) {
          // (workshopData[typedKey] as IHost[]).forEach((host, index) => {
          //   if (typeof host === "object") {
          //     Object.entries(host).forEach(([subKey, subValue]) => {
          //       if (subValue !== undefined && subValue !== null) {
          //         formData.append(
          //           `${typedKey}[${index}][${subKey}]`,
          //           subValue.toString()
          //         );
          //       }
          //     });
          //   }
          // });
          formData.append(typedKey, JSON.stringify(workshopData[typedKey]));
        } else if (typedKey === "attached_documents") {
          // Separate existing documents (by URL) from new files
          const existingDocs = (workshopData.attached_documents as FileOrAttachedDocument[]).filter(
            (doc): doc is AttachedDocument => !(doc instanceof File)
          );
          const newFiles = (workshopData.attached_documents as FileOrAttachedDocument[]).filter(
            (doc): doc is File => doc instanceof File
          );

          // Append existing document URLs (or IDs if your API prefers)
          if (existingDocs.length > 0) {
            formData.append('existing_documents', JSON.stringify(existingDocs.map(doc => ({ id: doc.id, file_url: doc.file_url, file_name: doc.file_name, file_type: doc.file_type, file_size: doc.file_size }))));
          }

          // Append new files
          newFiles.forEach((file) => {
            formData.append('new_attached_documents[]', file); // Use array notation for multiple files
          });
        }
        else if (typedKey === "image_url" && workshopData[typedKey] instanceof File) {
            formData.append(typedKey, workshopData[typedKey]);
        }
        else if (workshopData[typedKey] !== null && typeof workshopData[typedKey] !== 'undefined') {
            formData.append(typedKey, workshopData[typedKey].toString());
        }
      }
    }
    setIsSubmitting(true);
    apiRemote
      .updateWorkshop(formData) // Pass the ID and the updated data
      .then((data) => {
        if (data.code == "201") {
          // Assuming 200 for successful update
          alert.success(data.message);
        } else {
          alert.error(data.message);
        }
      })
      .catch((err) => {
        alert.error(err.message || "Failed to update workshop.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handlePublish = async (e: React.FormEvent) => {
    e.preventDefault();
    // alert.success("Workshop Published!")
    if (
      !workshopData.title ||
      !workshopData.description ||
      !workshopData.date ||
      // !workshopData.event_link ||
      !workshopData.topic ||
      workshopData.topic.length === 0 ||
      !workshopData.duration ||
      !workshopData.format ||
      !workshopData.category ||
      !workshopData.max_attendee ||
      !workshopData.learning_objectives || 
      !workshopData.target_audience
    ) {
      alert.error("Please fill in all required information");
      return;
    }

    if (!workshopData.image_url) {
      alert.error("Please upload an image for the workshop.");
      return;
    }
    const dataToSend = { ...workshopData, status: "published" };
    // Create a FormData object to send both text fields and files
    const formData = new FormData();

    // // Append all workshop data fields to formData
    for (const key in dataToSend) {
      // console.log(key, dataToSend.hasOwnProperty(key), dataToSend)
      if (dataToSend.hasOwnProperty(key)) {
        // Assert key as a valid key of dataToSend to resolve TypeScript error
        const typedKey = key as keyof typeof dataToSend;
        
        // Handle specific cases for non-string data
        if (typedKey === "topic") {
          formData.append(typedKey, JSON.stringify(dataToSend[typedKey]));
        } else if (typedKey === "hosts" && Array.isArray(dataToSend[typedKey])) {
          // (dataToSend[typedKey] as IHost[]).forEach((host, index) => {
          //   if (typeof host === "object") {
          //     Object.entries(host).forEach(([subKey, subValue]) => {
          //       if (subValue !== undefined && subValue !== null) {
          //         formData.append(
          //           `${typedKey}[${index}][${subKey}]`,
          //           subValue.toString()
          //         );
          //       }
          //     });
          //   }
          // });
          formData.append(typedKey, JSON.stringify(dataToSend[typedKey]));
        } else if (typedKey === "attached_documents") {
          // Separate existing documents (by URL) from new files
          const existingDocs = (dataToSend.attached_documents as FileOrAttachedDocument[]).filter(
            (doc): doc is AttachedDocument => !(doc instanceof File)
          );
          const newFiles = (dataToSend.attached_documents as FileOrAttachedDocument[]).filter(
            (doc): doc is File => doc instanceof File
          );

          // Append existing document URLs (or IDs if your API prefers)
          if (existingDocs.length > 0) {
            formData.append('existing_documents', JSON.stringify(existingDocs.map(doc => ({ id: doc.id, file_url: doc.file_url, file_name: doc.file_name, file_type: doc.file_type, file_size: doc.file_size }))));
          }

          // Append new files
          newFiles.forEach((file) => {
            formData.append('new_attached_documents[]', file); // Use array notation for multiple files
          });
        }
        else if (typedKey === "image_url" && dataToSend[typedKey] instanceof File) {
            formData.append(typedKey, dataToSend[typedKey]);
        }
        else if (dataToSend[typedKey] !== null && typeof dataToSend[typedKey] !== 'undefined') {
            formData.append(typedKey, dataToSend[typedKey].toString());
        }
      }
    }
    
    setIsPublishing(true);
    apiRemote
      .updateWorkshop(formData) // Pass the ID and the updated data
      .then((data) => {
        if (data.code == "201") {
          // Assuming 200 for successful update
          alert.success(data.message);
        } else {
          alert.error(data.message);
        }
      })
      .catch((err) => {
        alert.error(err.message || "Failed to update workshop.");
      })
      .finally(() => {
        setIsPublishing(false);
      });
  };
  // --- AI Integration Functions ---

  const handleGenerateSummary = async () => {
    if (!workshopData.title.trim()) {
      alert.warning(
        "Please enter a Workshop Title first to generate a summary."
      );
      return;
    }
    setIsGeneratingSummary(true);
    try {
      const response = await apiRemote.generateSummary({
        title: workshopData.title,
        brief_summary: workshopData.description
      });
      if(response.data.summary){
        setWorkshopData((prev) => ({
          ...prev,
          description: response.data.summary,
        }));
      }else{
        alert.error("We're sorry, we are currently experiencing an issue. Please try again in a few minutes.")
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      alert.error("Failed to generate summary. Please try again.");
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleGenerateObjectives = async () => {
    if (!workshopData.title.trim() || !workshopData.description.trim()) {
      alert.warning(
        "Please enter a Workshop Title and Brief Summary first to generate learning objectives."
      );
      return;
    }
    setIsGeneratingObjectives(true);
    try {
      const response = await apiRemote.generateLearningObjective({
        title: workshopData.title,
        summary: workshopData.description,
        learning_objectives: workshopData.learning_objectives
      });
      if (response.data && Array.isArray(response.data.learning_objectives)) {
        // Format the array into a bulleted string for the TextField
        const formattedObjectives = response.data.learning_objectives
          .map((obj: string) => `- ${obj}`) // Add a bullet point to each objective
          .join("\n"); // Join them with a newline
        setWorkshopData((prev) => ({
          ...prev,
          learning_objective: formattedObjectives,
        }));
      } else {
        alert.error("Invalid response format for learning objectives.");
      }
    } catch (error) {
      console.error("Error generating objectives:", error);
      alert.error("Failed to generate learning objectives. Please try again.");
    } finally {
      setIsGeneratingObjectives(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!aiImagePrompt.trim()) {
      alert.warning("Please enter a prompt to generate an image.");
      return;
    }
    setIsGeneratingImage(true);
    try {
      const response = await apiRemote.generateImage({
        prompt: aiImagePrompt,
      });
      setSelectedImage(response.data.image_urls[0]);
      setWorkshopData((prev) => ({
        ...prev,
        image_url: response.data.image_urls[0],
      }));
    } catch (error) {
      console.error("Error generating image:", error);
      alert.error("Failed to generate image. Please try again.");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validExtensions.includes(file.type)) {
        alert.error('Only accept files in .jpg, .jpeg, .png formats');
        return;
      }
    }

    if (!file) return;

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    if (file && file.size > maxFileSize) {
      alert.error('File size is too large. Please select a file smaller than 5MB.');
      event.target.value = ''; // Clear the input
      return;
    }

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);

      setWorkshopData(prev => ({
        ...prev,
        image_url: file
      }));
    }
  };

  const handleTopicsChange = (
    event: any,
    newValue: (string | { id: number; name: string })[]
  ) => {
    const formattedTopics = newValue.map((topic) =>
      typeof topic === "string" ? { id: Date.now(), name: topic } : topic
    );
    // Cập nhật formData.topics thay vì selectedTopics
    setWorkshopData((prev) => ({
      ...prev,
      topic: formattedTopics,
    }));
  };

  // File upload handler
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);

    // Tạo danh sách file mới kết hợp file cũ
    setWorkshopData(prev => ({
      ...prev,
      attached_documents: [...prev.attached_documents, ...newFiles]
    }));
  };

  // Remove file
  const handleRemoveFile = (index: number) => {
    setWorkshopData(prev => ({
      ...prev,
      attached_documents: prev.attached_documents.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Workshop Data...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Go Back
      </Button>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight={500}>
          Edit Workshop
        </Typography>
      </Box>

      <Card>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Section 1: Workshop Content */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                1
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Workshop Content
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Workshop Title"
              placeholder="e.g., The Official TCC Debrief"
              required
              sx={{
                mb: 3,
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              value={workshopData.title}
              onChange={(e) =>
                setWorkshopData((prev: any) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
            />

            <TextField
              fullWidth
              label="Brief Summary"
              required
              placeholder="A short, catchy summary for the workshop card..."
              multiline
              value={workshopData.description}
              minRows={3} // Bắt đầu với 3 dòng
              maxRows={10}
              sx={{
                mb: 3,
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              onChange={(e) => {
                setWorkshopData((prev: any) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }}
              InputProps={{
                endAdornment: (
                  <Button
                    startIcon={
                      isGeneratingSummary ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    size="small"
                    onClick={handleGenerateSummary}
                    disabled={isGeneratingSummary || !workshopData.title.trim()}
                  >
                    {isGeneratingSummary ? "Drafting..." : "Draft with AI"}
                  </Button>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Learning Objectives"
              required
              sx={{
                mb: 3,
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              placeholder="- Identify 3-5 practical AI tools..."
              multiline
              minRows={5} // Bắt đầu với 5 dòng
              maxRows={15}
              value={workshopData.learning_objectives}
              onChange={(e) =>
                setWorkshopData((prev: any) => ({
                  ...prev,
                  learning_objectives: e.target.value,
                }))
              }
              InputProps={{
                endAdornment: (
                  <Button
                    startIcon={
                      isGeneratingObjectives ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    size="small"
                    onClick={handleGenerateObjectives}
                    disabled={
                      isGeneratingObjectives ||
                      !workshopData.title.trim() ||
                      !workshopData.description.trim()
                    }
                  >
                    {isGeneratingObjectives ? "Generating..." : "Generate"}
                  </Button>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Target Audience"
              required
              placeholder="e.g., K-12 Science Teachers, School Administrators..."
              multiline
              sx={{
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              rows={2}
              value={workshopData.target_audience}
              onChange={(e) =>
                setWorkshopData((prev: any) => ({
                  ...prev,
                  target_audience: e.target.value,
                }))
              }
            />
          </CardContent>

          {/* Section 2: Host Details */}
          {isAdminOrOrg && (
            <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                  }}
                >
                  2
                </Box>
                <Typography variant="h4" fontWeight={500}>
                  Host Details
                </Typography>
              </Box>

              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <RadioGroup
                  row
                  value={workshopData.host_type}
                  onChange={(e) =>
                    setWorkshopData((prev: any) => ({
                      ...prev,
                      host_type: e.target.value,
                    }))
                  }
                >
                  <FormControlLabel
                    value="pythaverse"
                    control={<Radio />}
                    label="🏢 Pythaverse Educator"
                  />
                  <FormControlLabel
                    value="guest"
                    control={<Radio />}
                    label="🎤 Invited Guest"
                  />
                </RadioGroup>
              </FormControl>

              {workshopData.host_type == "pythaverse" ? (
                <Autocomplete
                  multiple
                  fullWidth
                  options={educators}
                  getOptionLabel={(option) => option.display_name + ' (' + option.user_email + ')'} // Display educator's name
                  filterOptions={(options, state) => {
                    // Lọc các lựa chọn để loại bỏ những người đã được chọn
                    const filteredOptions = options.filter(
                      (option) =>
                        !selectedEducators.some(
                          (selected) => selected.user_email === option.user_email
                        )
                    );
                    return filteredOptions;
                  }}
                  onInputChange={(event, newInputValue) => {
                    setSearchEducators(newInputValue); // Update search term to trigger debounced search
                    
                  }}
                  onChange={(event, newValue) => {
                    setSelectedEducators(newValue); // Set the selected educator object
                  }}
                  value={selectedEducators} // Controlled component: display the selected educator
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search and add Educators"
                      placeholder="Search and add Educators..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {debouncedSearchTerm &&
                            educators.length === 0 &&
                            !selectedEducators ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  // Optional: Render a custom option
                  renderOption={(props, option) => (
                    <li {...props}>
                      <ListItemText
                        primary={option.display_name}
                        secondary={option.user_email}
                      />
                    </li>
                  )}
                  noOptionsText={
                    searchEducators
                      ? "No educators found."
                      : "Start typing to Search and add Educatorss."
                  }
                />
              ) : (
                <Box>
                  {workshopData.hosts.map((host, index) => (
                      <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={10}>
                          <Grid container spacing={2}>
                              <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Guest's Full Name"
                                    value={host.host_name || ''}
                                    onChange={(e) => {
                                      const newHosts = [...workshopData.hosts];
                                      newHosts[index].host_name = e.target.value;
                                      setWorkshopData({ ...workshopData, hosts: newHosts });
                                    }}
                                  />
                              </Grid>
                              <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Guest's Email"
                                    value={host.host_email || ''}
                                    onChange={(e) => {
                                      const newHosts = [...workshopData.hosts];
                                      newHosts[index].host_email = e.target.value;
                                      setWorkshopData({ ...workshopData, hosts: newHosts });
                                    }}
                                  />
                              </Grid>
                              <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Guest's Title & Organization"
                                    value={host.host_organization || ''}
                                    onChange={(e) => {
                                      const newHosts = [...workshopData.hosts];
                                      newHosts[index].host_organization = e.target.value;
                                      setWorkshopData({ ...workshopData, hosts: newHosts });
                                    }}
                                  />
                              </Grid>
                              <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Guest's Professional Bio"
                                    multiline
                                    rows={3}
                                    value={host.host_bio || ''}
                                    onChange={(e) => {
                                      const newHosts = [...workshopData.hosts];
                                      newHosts[index].host_bio = e.target.value;
                                      setWorkshopData({ ...workshopData, hosts: newHosts });
                                    }}
                                  />
                              </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <IconButton onClick={() => handleRemoveGuest(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                  ))}
                  <Button onClick={handleAddGuest} variant="contained" startIcon={<AddIcon />}>
                    Add Guest
                  </Button>
                </Box>
              )}
            </CardContent>
          )}

          {/* Section 3: Logistics & Settings */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {isAdminOrOrg ? 3 : 2}
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Logistics & Settings
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Workshop Date"
                  type="datetime-local"
                  value={workshopData.date}
                  onChange={(e) =>
                    setWorkshopData({ ...workshopData, date: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (in minutes)"
                  type="number"
                  placeholder="e.g., 60"
                  required
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                  value={workshopData.duration}
                  onChange={(e) =>{
                    const value = Number(e.target.value);
                    // Chỉ cập nhật giá trị nếu nó không âm
                    if (value >= 0) {
                      setWorkshopData((prev) => ({
                        ...prev,
                        duration: value,
                      }));
                    }
                  }}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Format <span style={{color: "red"}}>*</span></InputLabel>
                  <Select
                    defaultValue="online"
                    label="Format"
                    required
                    value={workshopData.format}
                    onChange={(e) =>
                      setWorkshopData((prev: any) => ({
                        ...prev,
                        format: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="in-person">In-Person</MenuItem>
                    <MenuItem value="hybrid">Hybrid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category <span style={{color: "red"}}>*</span></InputLabel>
                  <Select
                    defaultValue="ai"
                    label="Category"
                    required
                    value={workshopData.category}
                    onChange={(e) =>
                      setWorkshopData((prev: any) => ({
                        ...prev,
                        category: e.target.value,
                      }))
                    }
                  >
                    {categories.map((category, index) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Attendee Limit"
                  type="number"
                  placeholder="Leave blank for unlimited"
                  required
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                  value={workshopData.max_attendee}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    // Chỉ cập nhật giá trị nếu nó không âm
                    if (value >= 0) {
                      setWorkshopData((prev) => ({
                        ...prev,
                        max_attendee: value,
                      }));
                    }
                  }}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Live Event Link"
                  type="text"
                  // required
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                  value={workshopData.event_link}
                  onChange={(e) =>
                    setWorkshopData((prev: any) => ({
                      ...prev,
                      event_link: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={topics}
                  getOptionLabel={(option: any) => option.name}
                  value={workshopData.topic} // Thay thế selectedTopics bằng formData.topics
                  isOptionEqualToValue={(option, value) =>
                    option.id == value.id
                  }
                  onChange={handleTopicsChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={
                        <>
                          Workshop Tags <span style={{ color: "red" }}>*</span>
                        </>
                      }
                      placeholder="Type a tag and press Enter"
                      // Thêm helperText ở đây để hướng dẫn người dùng
                      helperText="Add tags to help educators discover your workshop"
                      sx={{
                        '& .MuiFormHelperText-root': {
                          // fontWeight: 'bold', // Ví dụ: in đậm chữ
                          fontSize: '0.9rem', // Ví dụ: tăng kích thước chữ
                          // Bạn có thể thêm các thuộc tính CSS khác tùy ý
                        },
                      }}
                    />
                  )}
                  renderTags={(topicValue, getTagProps) =>
                    topicValue.map((option, index) => (
                      <Chip label={option.name} {...getTagProps({ index })} />
                    ))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Video Link"
                  type="text"
                  value={workshopData.video_recording_url}
                  onChange={(e) =>
                    setWorkshopData((prev: any) => ({
                      ...prev,
                      video_recording_url: e.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          </CardContent>

          {/* Section 4: Cover Image */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {isAdminOrOrg ? 4 : 3}
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Cover Image
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Generate Image with AI"
              placeholder="e.g., 'A modern classroom with glowing data visualizations'"
              value={aiImagePrompt}
              onChange={(e) => setAiImagePrompt(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <Button
                    startIcon={
                      isGeneratingImage ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    size="small"
                    onClick={handleGenerateImage}
                    disabled={isGeneratingImage || !aiImagePrompt.trim()} // Disable if prompt is empty or generating
                  >
                    {isGeneratingImage ? "Generating..." : "Generate"}
                  </Button>
                ),
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                (or)
              </Typography>
              <Button
                component="label" // Use label to trigger file input
                variant="outlined"
                startIcon={<UploadFileIcon />}
              >
                Upload image manually
                <input
                  type="file"
                  hidden
                  accept="image/*" // Accept only image files
                  onChange={handleImageUpload}
                />
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                {selectedImage ? (
                  <Box
                    component="img"
                    src={selectedImage}
                    alt="Selected Cover Image"
                    sx={{
                      width: "100%",
                      borderRadius: 1,
                      border: 3,
                      borderColor: "primary.main",
                      minHeight: 300,
                      objectFit: "fill",
                      maxHeight: 700
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: 169, // Maintain aspect ratio roughly
                      borderRadius: 1,
                      border: 3,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.100",
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body2">No image selected</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>

          {/* Attached Documents */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {isAdminOrOrg ? 5 : 4}
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Resources
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <input
                accept="image/*,application/pdf,.doc,.docx,.zip"
                style={{ display: 'none' }}
                id="upload-documents"
                type="file"
                multiple
                onChange={handleFileUpload}
              />
              <label htmlFor="upload-documents">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                >
                  Upload documents
                </Button>
              </label>
            </Box>

            {workshopData.attached_documents && workshopData.attached_documents.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Uploaded files:
                </Typography>
                {workshopData.attached_documents.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <Typography variant="body2">{file instanceof File ? file.name : file.file_name}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>

          {/* Actions */}
          <Box
            sx={{
              p: 3,
              bgcolor: "grey.50",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            {workshopData.status == 'draft' && (
              <Button variant="outlined" disabled={isPublishing} onClick={handlePublish}>
                {isPublishing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Publish"
                )}
              </Button>
            )}
            
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Box>
      </Card>
    </Container>
  );
}
